import React, { useState, useCallback, useEffect, useContext } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import Form from 'react-bootstrap/Form'
import Tab from 'react-bootstrap/Tab'
import Row from 'react-bootstrap/Row'
import Button from 'react-bootstrap/Button'

import { StepContext } from '../StepContext'
import { TabsBimestre, DeduccionesFA } from './UtilsFieldArray'
import { TotalBimestre, TotalAnual } from './Totales'
import CargandoDatosLoader from '../components/CargandoDatosLoader'
import { getFirstError, goToFirstError } from '../components/Errores'

import { LEGAJO_UNICO } from 'utils/legajo'
import { mainRequest, emptyPromise } from '../utils/requests'

const defaultValues = [1, 2, 3, 4, 5, 6].reduce((acc, n) => {
  let item = {}
  item[`bimestre${n}`] = {
    name: `bimestre${n}`,
    bimestre: n,
    legajos: []
  }
  return { ...acc, ...item }
}, {})

function DeduccionesExclusiones() {
  const { setPuedeAvanzar, setCallback } = useContext(StepContext)
  const [loading, setLoading] = useState(true)
  const [bimestreActivo, setBimestreActivo] = useState(1)

  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues
  })

  const [exclusiones, setExclusiones] = useState([])
  const [deducciones, setDeducciones] = useState([])
  const [legajos, setLegajos] = useState([])

  const { getValues, reset, trigger, errors } = methods

  const storeData = useCallback(async () => {
    if (!methods.formState.isValid) {
      trigger()
      return emptyPromise()
    }

    return mainRequest('/ddjj/api/deducciones-exclusiones', {
      method: 'POST',
      body: JSON.stringify(getValues())
    }).then(({ status, content }) => status === 200)
  }, [getValues, trigger, methods.formState.isValid])

  useEffect(() => {
    setCallback(storeData)
    setPuedeAvanzar(true)
  }, [setPuedeAvanzar, setCallback, storeData])

  useEffect(() => {
    let posible = true
    setLoading(true)

    fetch('/ddjj/api/deducciones-exclusiones')
      .then((res) => res.json())
      .then((res) => {
        if (posible) {
          setExclusiones(
            res.exclusiones.map((e) => ({
              id: e.excID,
              nombre: e.excDescripcion
            }))
          )
          setDeducciones(
            res.deducciones.map((d) => ({
              id: d.dedID,
              nombre: d.dedDescripcion
            }))
          )
          setLegajos(res.legajos)

          // Precargar deducciones y exclusiones preexistentes
          const bimestres = res.dataPrevia.reduce(
            (acc, element) => {
              let item = {
                bimestre: element.bimestre,
                legajo: element.legajo,
                importe: element.importe
              }
              let prop = element.tipo === 'D' ? 'deduccion' : 'exclusion'
              item[prop] = element.id

              const name = `bimestre${item.bimestre}`
              const legajoIndex = res.legajos.indexOf(item.legajo)
              let property =
                element.tipo === 'D' ? 'deducciones' : 'exclusiones'

              let bimestres = acc

              if (bimestres[name]?.legajos?.[legajoIndex]?.[property]) {
                bimestres[name].legajos[legajoIndex][property].push(item)
                return bimestres
              }

              if (bimestres[name]?.legajos?.[legajoIndex]) {
                bimestres[name].legajos[legajoIndex][property] = [item]
                return bimestres
              }

              if (bimestres[name]?.legajos) {
                bimestres[name].legajos[legajoIndex] = {}
                bimestres[name].legajos[legajoIndex][property] = [item]
                return bimestres
              }

              return bimestres
            },
            {
              bimestre1: { legajos: [] },
              bimestre2: { legajos: [] },
              bimestre3: { legajos: [] },
              bimestre4: { legajos: [] },
              bimestre5: { legajos: [] },
              bimestre6: { legajos: [] }
            }
          )

          reset(bimestres)
          setLoading(false)
        }
      })

    return () => {
      posible = false
    }
  }, [setLoading, setExclusiones, setDeducciones, reset])

  return (
    <>
      <h6 className="text-muted text-normal text-uppercase mb-4">
        Datos de exclusiones y deducciones
      </h6>

      {getFirstError(errors) !== null ? (
        <Button
          style={{ position: 'fixed', bottom: '0', right: '0', zIndex: 99 }}
          onClick={() => goToFirstError(errors, setBimestreActivo)}
        >
          Ir al siguiente error
        </Button>
      ) : (
        <></>
      )}

      <CargandoDatosLoader cargando={loading}>
        <FormProvider {...methods}>
          <Form onSubmit={methods.handleSubmit(() => console.log(getValues()))}>
            <Tab.Container
              activeKey={bimestreActivo}
              onSelect={(n) => setBimestreActivo(n)}
            >
              <TabsBimestre values={Object.values(defaultValues)} />

              <Row className="container-fluid">
                <Tab.Content className="w-100">
                  {Object.values(defaultValues).map((state, index) => (
                    <Tab.Pane
                      key={`${state.name}.pane${index}`}
                      eventKey={state.bimestre}
                    >
                      {legajos.map((legajo, legajoIndex) => (
                        <React.Fragment key={`leg${legajoIndex}`}>
                          <h4 className="text-muted text-uppercase text-center"
			      style={{display: legajo === LEGAJO_UNICO ? 'none' : 'block'}}>
                            {`Legajo ${legajo}`}
                          </h4>
                          <hr className="m-0 mb-4" />
                          <input
                            style={{ display: 'none' }}
                            name={`bimestre${state.bimestre}.legajos.[${legajoIndex}].legajo`}
                            defaultValue={legajo}
                            ref={methods.register({ required: false })}
                          />

                          <DeduccionesFA
                            name={`bimestre${state.bimestre}.legajos.[${legajoIndex}].exclusiones`}
                            bimestre={`bimestre${state.bimestre}`}
                            arrayName={`exclusiones`}
                            property="exclusion"
                            legajo={legajo}
                            legajoIndex={legajoIndex}
                            text="Exclusión"
                            options={exclusiones}
                          />

                          <DeduccionesFA
                            name={`bimestre${state.bimestre}.legajos.[${legajoIndex}].deducciones`}
                            bimestre={`bimestre${state.bimestre}`}
                            arrayName={`deducciones`}
                            property="deduccion"
                            legajo={legajo}
                            legajoIndex={legajoIndex}
                            text="Deducción"
                            options={deducciones}
                          />
                        </React.Fragment>
                      ))}
                      <TotalBimestre
                        titulo={`Total Bimestre ${state.bimestre}`}
                        bimestre={`bimestre${state.bimestre}`}
                        title1="exclusiones"
                        title2="deducciones"
                      />
                    </Tab.Pane>
                  ))}
                </Tab.Content>
              </Row>

              <TotalAnual title1="exclusiones" title2="deducciones" />
            </Tab.Container>
          </Form>
        </FormProvider>
      </CargandoDatosLoader>
    </>
  )
}

export default DeduccionesExclusiones
